// databox product update
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { APIData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getFormattedTime, getRelativeTime } from 'webapp/utils/number';

import ToolTipComponent from 'webapp/components/tool-tip';

import type { ProductUpdate } from 'webapp/services/product-updates';

interface Args {
	model: ProductUpdate;
	isMobileApp?: boolean;
	originForProductUpdatesClicked?: string; // 'read_more'
}
export default class MonitoringPanelsUpdatesItemComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('product-updates') declare productUpdatesService: Service.ProductUpdates;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	updateBaseUrl = 'https://databox.com/product-updates'; // 'https://updates.databox.com'

	get update() {
		return this.args.model.data.productUpdate;
	}

	get updateMessageUrl() {
		return this.updateBaseUrl + '#u' + this.args.model.id;
	}

	get showTag() {
		const tag = this.update?.tag;
		return tag !== 'no tag' && tag !== '';
	}

	get createdTooltipText() {
		const tz = this.currentUserService.model?.timezone;
		return getFormattedTime(this.args.model.created, tz, 'MMM D, YYYY [at] hh:mm A');
	}

	get relativeTimeText() {
		return this.args.model.created ? getRelativeTime(this.args.model.created) : '';
	}

	@action
	trackProductUpdateOpen() {
		if (this.args.originForProductUpdatesClicked) {
			this.analyticsService.event(TrackingEvent.PRODUCT_UPDATES_CLICKED, {
				origin: this.args.originForProductUpdatesClicked,
			});
		} else {
			this.trackActivityEvent({
				type: 'product_update_open',
			});
		}
	}

	@action
	saveProductUpdatesSeen() {
		this.productUpdatesService.saveProductUpdatesSeen();
	}

	private trackActivityEvent(data: APIData.Tracking.Data) {
		this.analyticsService.event(TrackingEvent.INSIGHTS_ACTIVITY_MESSAGE_ACTION, data);
	}
	<template>
		<div role="button" class="product-update" {{on "click" this.trackProductUpdateOpen}}>

			<div class="product-update__top">
				<span class="text">
					<span>
						{{#if this.showTag}}
							<span class="tag tag-{{this.update.tag}}">
								{{this.update.tag}}
							</span>
						{{/if}}
					</span>
					<span>
						<small class="time">{{this.relativeTimeText}}</small>
						<ToolTipComponent
							@text={{this.createdTooltipText}}
							@position="bottom"
							@maxWidth={{200}}
						/>
					</span>
				</span>
			</div>

			<div class="product-update__title">{{this.update.title}}</div>

			{{#if this.update.image}}
				{{! template-lint-disable no-nested-interactive }}
				<a
					href={{this.updateMessageUrl}}
					{{on "click" this.saveProductUpdatesSeen}}
					target="_blank"
				>
					<img src={{this.update.image}} alt={{this.update.title}} />
				</a>
			{{/if}}

			{{#if this.update.shortText}}
				<p class="product-update__short-text">{{this.update.shortText}}</p>
			{{/if}}

			<div class="product-update__actions">
				{{! template-lint-disable no-nested-interactive }}
				<a
					href={{this.updateMessageUrl}}
					{{on "click" this.saveProductUpdatesSeen}}
					target="_blank"
				>
					Read more
				</a>
			</div>
		</div>
	</template>
}
